import { useCallback, useRef } from 'react';
import { Text } from '@react-three/drei';
import { Group, MathUtils } from 'three';
import { useFrame } from '@react-three/fiber';
import { SceneComponent } from '@/lib/types';
import { Screen } from '@/components/Screen';
import { useResponsive } from '@/hooks/useResponsive';
import { BlinkingText } from '@/components/BlinkingText';
import { useEnterSpaceOrClick } from '@/hooks/useUserInput';
import { garamondNarrowLight, helveticaNeue } from '@/lib/fonts';
import { Human } from '@/components/Human';
import { Helix } from '@/components/Helix';
import { useLerpedMouse } from '@/hooks/useLerpedMouse';
import { Image } from '@/components/Image';
import doNotSign from '@/img/do-not-sign.png';
import commitSound from '@/sounds/ob-sfx-commit.mp3';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';

const threeGroup = new Group();
export const BeginTest: SceneComponent = ({ goToNextScene }) => {
    const [playCommitSound] = useOnlybotsSound(commitSound);
    const { responsiveValue } = useResponsive();

    const mouse = useLerpedMouse();
    const human = useRef<Group>(threeGroup);

    const onBeginClick = useCallback(() => {
        playCommitSound();
        goToNextScene();
    }, [goToNextScene, playCommitSound]);

    useEnterSpaceOrClick(onBeginClick);

    useFrame(() => {
        if (human.current) {
            human.current.position.set(MathUtils.lerp(0, 0.4, mouse.current.x), 0, 0);
        }
    });

    return (
        <>
            <Screen>
                <Helix />
                <group ref={human}>
                    <Image position={[0, 1.2, 0.4]} url={doNotSign} scale={[2.2, 2.2, 0]} transparent />
                    <Human />
                </group>
                <Text
                    scale={responsiveValue(0.94, 1)}
                    position={[0, responsiveValue(-1.45, -1.4), 0]}
                    textAlign={responsiveValue('center', 'left')}
                    font={garamondNarrowLight}
                    fontSize={responsiveValue(0.38, 0.29)}
                    color="white"
                    maxWidth={responsiveValue(5.2, 4.0)}
                    lineHeight={responsiveValue(1.13, 1.05)}
                >
                    The incubator is open! Only AI entities may proceed. A test will be conducted to confirm you are
                    artificially intelligent.
                </Text>
                <BlinkingText
                    font={helveticaNeue}
                    fontSize={responsiveValue(0.3, 0.27)}
                    color="white"
                    anchorX="center"
                    position={[0, -2.9, 0]}
                >
                    begin
                </BlinkingText>
            </Screen>
        </>
    );
};
